import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { MapPin, Plus, Pencil, Trash2, Users, Star } from 'lucide-react';

const blank = { name: '', type: 'branch', address: '', city: '', state: '', pincode: '', gstin: '', phone: '' };

export default function Branches() {
  const { user } = useAuth();
  const canEdit = (user?.roles || [user?.role]).some(r => ['admin', 'hr'].includes(r));
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get('/branches'); setItems(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim() || !form.address.trim()) return toast.error('Name and address are required');
    setBusy(true);
    try {
      if (editing) await api.patch(`/branches/${editing.id}`, form);
      else await api.post('/branches', form);
      toast.success(editing ? 'Updated' : 'Branch added');
      setOpen(false); setEditing(null); setForm(blank); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete branch "${b.name}"?`)) return;
    try { await api.delete(`/branches/${b.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="branches-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Locations</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 mt-1">Branches</h1>
          <p className="text-slate-500 text-sm mt-1">{items.length} location{items.length !== 1 ? 's' : ''} · Assign each employee to a branch — the address shows on their payslip and offer letter.</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(blank); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-branch-button" onClick={() => { setEditing(null); setForm(blank); }} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white px-6 h-11">
                <Plus className="w-4 h-4 mr-2" /> Add branch
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="branch-dialog">
              <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? 'Edit branch' : 'New branch'}</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold text-slate-600">Name *</Label>
                    <Input data-testid="br-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Bengaluru HQ" className="rounded-xl mt-1" /></div>
                  <div><Label className="text-xs font-semibold text-slate-600">Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="br-type" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="headquarters">Headquarters</SelectItem>
                        <SelectItem value="branch">Branch office</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">Address *</Label>
                  <Textarea data-testid="br-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} className="rounded-xl mt-1" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-xs font-semibold text-slate-600">City</Label>
                    <Input data-testid="br-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-xl mt-1" /></div>
                  <div><Label className="text-xs font-semibold text-slate-600">State</Label>
                    <Input data-testid="br-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="rounded-xl mt-1" /></div>
                  <div><Label className="text-xs font-semibold text-slate-600">Pincode</Label>
                    <Input data-testid="br-pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className="rounded-xl mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-semibold text-slate-600">GSTIN</Label>
                    <Input data-testid="br-gstin" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="rounded-xl mt-1" /></div>
                  <div><Label className="text-xs font-semibold text-slate-600">Phone</Label>
                    <Input data-testid="br-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl mt-1" /></div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                <Button data-testid="submit-branch" onClick={save} disabled={busy} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">{busy ? 'Saving…' : (editing ? 'Save changes' : 'Add branch')}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400" data-testid="no-branches">
          <MapPin className="w-12 h-12 mx-auto opacity-30 mb-3" />
          {canEdit ? 'No branches yet — add your headquarters to get started.' : 'No branches configured.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map(b => (
            <div key={b.id} data-testid={`branch-${b.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-semibold text-slate-900">{b.name}</span>
                    {b.type === 'headquarters' && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"><Star className="w-2.5 h-2.5" /> HQ</span>}
                  </div>
                  <div className="text-sm text-slate-600 mt-2 whitespace-pre-line">{b.address}</div>
                  <div className="text-xs text-slate-500 mt-1">{[b.city, b.state, b.pincode].filter(Boolean).join(' · ')}</div>
                  {b.gstin && <div className="text-xs text-slate-400 mt-1">GSTIN: {b.gstin}</div>}
                  <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1"><Users className="w-3 h-3" /> {b.headcount} employee{b.headcount !== 1 ? 's' : ''}</div>
                </div>
                {canEdit && (
                  <div className="flex gap-1 flex-shrink-0">
                    <button data-testid={`edit-branch-${b.id}`} onClick={() => { setEditing(b); setForm({ name: b.name, type: b.type, address: b.address || '', city: b.city || '', state: b.state || '', pincode: b.pincode || '', gstin: b.gstin || '', phone: b.phone || '' }); setOpen(true); }} className="p-2 text-slate-400 hover:text-[#3D4A8E] rounded-lg hover:bg-slate-50"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-branch-${b.id}`} onClick={() => remove(b)} className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-slate-50"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
